import type { DmConversation } from '@/types/dm';

/**
 * Lo swipe di una riga dell'elenco dei messaggi (v2, L-2238 A1 (c) e A2 (a), Luca ~23:2xZ del 25/09) — la parte
 * PURA: quali azioni, in che ordine, e cosa fa un gesto finito. Il componente (`components/dm/RigaConversazione.tsx`)
 * muove la riga e chiede qui. Riferimento eseguibile: il prototipo v2 (KB `attachments/2026-09-25-dm-prototipo-v2.html`).
 *
 * Il lato e' quello verso cui va il DITO: «swipe a sinistra» scopre i bottoni sul bordo destro, e viceversa.
 * - a **sinistra** → Profilo · **Blocca** (principale; SEMPRE con conferma, anche col gesto lungo);
 * - a **destra** → **Segna come letto** (principale, solo con non letti) · Sfida.
 * La principale e' la piu' ESTERNA (come Mail e WhatsApp): a destra la prima, a sinistra l'ultima.
 * A2 (a): nessuna azione che QSocials non ha (Silenzia, Archivia, Fissa, Non letto, Elimina: fuori).
 */
export type AzioneRiga = 'letto' | 'sfida' | 'profilo' | 'blocca';
export type LatoSwipe = 'sinistra' | 'destra';

/** Sotto questo spostamento orizzontale il gesto e' ancora un tocco (o uno scorrimento verticale). */
export const SOGLIA_PRESA_PX = 8;
/** Da qui in su, lasciando, la riga resta aperta sui suoi bottoni. */
export const SOGLIA_APERTURA_PX = 40;
/** Oltre questa frazione della larghezza, lasciando, parte la principale. */
export const FRAZIONE_LUNGO = 0.6;
export const LARGHEZZA_AZIONE_PX = 74;

/**
 * «Sfida» ha il suo posto nello swipe a destra, e c'e' solo dove la sfida di piattaforma e' aperta: non e' una
 * costante di questo file ma un'OPZIONE di `azioniRiga`, e chi la passa e' `ElencoConversazioni`, che la legge
 * dalle STESSE serrature del pannello di Amici (`useSfidaDiPiattaforma`: flag Limulo della build e ruolo beta).
 * A serrature chiuse (la produzione) il bottone non esiste (L-2238 A1 (c): «si accende solo quando esiste una
 * sfida vera»).
 */
export function azioniRiga(
  c: Pick<DmConversation, 'closed' | 'unread'>,
  opzioni: { sfida: boolean },
): Record<LatoSwipe, AzioneRiga[]> {
  // Chiusa (§1-ter): niente da leggere, niente da bloccare — resta il profilo.
  if (c.closed === true) return { destra: [], sinistra: ['profilo'] };
  const destra: AzioneRiga[] = [];
  if (c.unread > 0) destra.push('letto');
  if (opzioni.sfida) destra.push('sfida');
  return { destra, sinistra: ['profilo', 'blocca'] };
}

/** La principale di un lato: la piu' esterna. `null` se il lato non ha azioni. */
export function principale(lato: LatoSwipe, azioni: readonly AzioneRiga[]): AzioneRiga | null {
  if (azioni.length === 0) return null;
  return lato === 'destra' ? azioni[0] : azioni[azioni.length - 1];
}

/** Il lato di uno spostamento (positivo = il dito va a destra). */
export const latoDi = (x: number): LatoSwipe | null => (x > 0 ? 'destra' : x < 0 ? 'sinistra' : null);

/** Lo spostamento consentito: niente verso un lato senza azioni, mai oltre la larghezza della riga. */
export function spostamentoConsentito(
  x: number,
  larghezza: number,
  n: Record<LatoSwipe, number>,
): number {
  let y = x;
  if (n.destra === 0) y = Math.min(y, 0);
  if (n.sinistra === 0) y = Math.max(y, 0);
  return Math.max(-larghezza, Math.min(larghezza, y));
}

/** Dove sta la riga aperta su un lato: i bottoni scoperti per intero. */
export function posizioneAperta(lato: LatoSwipe | null, n: Record<LatoSwipe, number>): number {
  if (lato === 'destra') return n.destra * LARGHEZZA_AZIONE_PX;
  if (lato === 'sinistra') return -n.sinistra * LARGHEZZA_AZIONE_PX;
  return 0;
}

export type EsitoGesto =
  | { tipo: 'principale'; lato: LatoSwipe }
  | { tipo: 'apri'; lato: LatoSwipe }
  | { tipo: 'chiudi' };

/** Cosa fa un gesto finito a `x` (gia' passato da `spostamentoConsentito`). */
export function esitoGesto(x: number, larghezza: number, n: Record<LatoSwipe, number>): EsitoGesto {
  const lato = latoDi(x);
  if (lato === null || n[lato] === 0) return { tipo: 'chiudi' };
  const d = Math.abs(x);
  if (d > larghezza * FRAZIONE_LUNGO) return { tipo: 'principale', lato };
  if (d > SOGLIA_APERTURA_PX) return { tipo: 'apri', lato };
  return { tipo: 'chiudi' };
}

/**
 * Il gesto e' uno swipe (e la riga si prende il puntatore) solo quando l'orizzontale supera la soglia E
 * vince sul verticale: altrimenti e' un tocco, o lo scorrimento della pagina.
 */
export const eSwipe = (dx: number, dy: number): boolean =>
  Math.abs(dx) >= SOGLIA_PRESA_PX && Math.abs(dx) >= Math.abs(dy);
